import type { ReactNode } from 'react';
import { useState } from 'react';
import { Box, Text, Stack, Button, Select, Radio, Checkbox, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { NAVY, inputStyles } from './styles';
import { uniqueOptions } from '@/features/editor/utils/multiChoiceOptions';

interface MultiChoiceElementProps {
  element: any;
  value: any;
  label: ReactNode;
  onChange: (value: any) => void;
  onBranchSelect: (targetPageId: string, value: any) => void;
}

export function MultiChoiceElement({ element, value, label, onChange, onBranchSelect }: MultiChoiceElementProps) {
  // Remembered here because `value` only changes when the element has a
  // variable bound; the note must show either way.
  const [pickedUnconnected, setPickedUnconnected] = useState(false);
  if (element.createBranches && !element.allowMultiple) {
    // Branching MultiChoice acts like a navigation menu. An option the author
    // has not pointed at a page yet only gets selected; say so.
    const unconnected = pickedUnconnected;
    return (
      <Box>
        <Text size="sm" fw={500} mb="xs" c="#2A3140">{label}</Text>
        <Stack
          gap={0}
          style={{
            borderTop: '1px solid #D9DEE8',
            backgroundColor: '#FFFFFF',
          }}
        >
          {(element.options || []).map((option: string, idx: number) => {
            const branch = (element.branches || []).find((b: any) => b.option === option);
            return (
              <UnstyledButton
                key={idx}
                style={{
                  width: '100%',
                  borderBottom: '1px solid #D9DEE8',
                  minHeight: 48,
                  padding: '11px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  color: value === option ? NAVY : '#2A3140',
                  backgroundColor: value === option ? '#EEF3FF' : '#FFFFFF',
                  transition: 'background-color 120ms ease',
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = value === option ? '#EEF3FF' : '#F7F9FC';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = value === option ? '#EEF3FF' : '#FFFFFF';
                }}
                onClick={() => {
                  setPickedUnconnected(!branch?.targetPageId);
                  if (branch?.targetPageId) {
                    onBranchSelect(branch.targetPageId, option);
                  } else {
                    onChange(option);
                  }
                }}
              >
                <Text size="md" fw={value === option ? 700 : 500} style={{ lineHeight: 1.35 }}>
                  {option}
                </Text>
                <IconChevronRight size={18} color={NAVY} style={{ flexShrink: 0 }} />
              </UnstyledButton>
            );
          })}
        </Stack>
        {unconnected && (
          <Text size="sm" mt={6} c="orange.8" role="status">
            This option isn't connected to a page yet.
          </Text>
        )}
      </Box>
    );
  }
  // Non-branching MultiChoice
  if (element.displayStyle === 'dropdown') {
    return (
      <Select
        label={label}
        placeholder="Select an option"
        data={uniqueOptions(element.options || [])}
        value={value || null}
        onChange={(val) => onChange(val)}
        styles={inputStyles}
        radius="md"
      />
    );
  }
  if (element.displayStyle === 'buttons') {
    return (
      <Box>
        <Text size="sm" fw={500} mb="xs" c="#2A3140">{label}</Text>
        <Stack gap="xs">
          {(element.options || []).map((option: string, idx: number) => (
            <Button
              key={idx}
              variant={value === option ? 'filled' : 'outline'}
              color={NAVY}
              fullWidth
              justify="flex-start"
              radius="md"
              onClick={() => onChange(option)}
            >
              {option}
            </Button>
          ))}
        </Stack>
      </Box>
    );
  }
  if (element.displayStyle === 'checkbox' || element.allowMultiple) {
    return (
      <Box>
        <Text size="sm" fw={500} mb="xs" c="#2A3140">{label}</Text>
        <Checkbox.Group value={Array.isArray(value) ? value : []} onChange={onChange}>
          <Stack gap="xs">
            {(element.options || []).map((opt: string, i: number) => (
              <Checkbox key={i} value={opt} label={opt} color={NAVY} />
            ))}
          </Stack>
        </Checkbox.Group>
      </Box>
    );
  }
  // Radio style
  return (
    <Box>
      <Text size="sm" fw={500} mb="xs" c="#2A3140">{label}</Text>
      <Radio.Group value={value || ''} onChange={onChange}>
        <Stack gap="xs">
          {(element.options || []).map((opt: string, i: number) => (
            <Radio key={i} value={opt} label={opt} color={NAVY} />
          ))}
        </Stack>
      </Radio.Group>
    </Box>
  );
}
